import { Typography } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';

import type { StoreSpecial } from '@jane/bloom/types/schemas/marketplace/storeSpecials';

import styles from './dealsBanner.module.css';

type Accumulator = {
  bulkSpecials: StoreSpecial[];
  cartTotalSpecials: StoreSpecial[];
  groupSpecials: StoreSpecial[];
};

export function DealsBanner({
  storeSpecials,
}: {
  storeSpecials: StoreSpecial[];
}) {
  const { bulkSpecials, cartTotalSpecials, groupSpecials } =
    storeSpecials.reduce<Accumulator>(
      (acc, special) => {
        if (special.special_type === 'cart_total') {
          acc.cartTotalSpecials.push(special);
        } else if (special.special_type === 'bulk_pricing') {
          acc.bulkSpecials.push(special);
        } else if (special.special_type === 'qualified_group') {
          acc.groupSpecials.push(special);
        }
        return acc;
      },
      { bulkSpecials: [], cartTotalSpecials: [], groupSpecials: [] }
    );

  const contentRef = useRef<HTMLDivElement>(null);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const container = contentRef.current;

    const handleScroll = () => {
      if (container) {
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const containerWidth = container.offsetWidth;
        const marginOfError = 10;

        const isAtEnd =
          scrollLeft + containerWidth >= scrollWidth - marginOfError;

        setAtEnd(isAtEnd);
      }
    };

    container?.addEventListener('scroll', handleScroll);

    // Initial check for scrollability on component mount to handle edge cases
    handleScroll();

    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={styles['bannerContainer']}>
      <div
        className={clsx(styles['bannerContent'], {
          [styles['fadeRight']]: !atEnd,
          [styles['noFade']]: atEnd,
        })}
        ref={contentRef}
      >
        {cartTotalSpecials.length > 0 && (
          <>
            <Typography variant="body-bold" color="inherit">
              Cart total
            </Typography>
            {cartTotalSpecials.map((special, i) => {
              return (
                <Typography color="inherit" key={i}>
                  {special.title}
                </Typography>
              );
            })}
          </>
        )}
        {bulkSpecials.length > 0 && (
          <>
            <Typography variant="body-bold" color="inherit">
              Bulk
            </Typography>
            {bulkSpecials.map((special, i) => {
              return (
                <Typography color="inherit" key={i}>
                  {special.title}
                </Typography>
              );
            })}
          </>
        )}
        {groupSpecials.length > 0 && (
          <>
            <Typography variant="body-bold" color="inherit">
              Group discount
            </Typography>
            {groupSpecials.map((special, i) => {
              return (
                <Typography color="inherit" key={i}>
                  {special.title}
                </Typography>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
